  import { apiService } from '@/lib/api-functions/api-service';
import { Game } from '@/types-games';
import { unifiedUserStore } from './unifiedUserStore';

/**
 * Update game information through the API
 */
export async function updateGameInfo(
  gameId: string,
  updates: Partial<Game>,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<Game> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Call the API to update the game
    const result = await apiService.updateGame(gameId, {
      name: updates.name,
      description: updates.description,
      settings: updates.settings
    });
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update game');
    }

    // Convert API response to Game interface
    const updatedGame: Game = {
      id: result.data.id,
      name: result.data.name,
      description: result.data.description,
      settings: result.data.settings || { // TODO: Add settings to Game interface
        languages: [],
        deadline: '',
        sections: [],
        defaultLanguage: ''
      },
      created_at: result.data.created_at,
      last_updated: result.data.last_updated,
      members: [], // API doesn't return members in update response // TODO: Add members to Game interface
      game_stats: updates.game_stats || { // TODO: Add game_stats to Game interface
        total_strings: 0,
        translated_strings: 0,
        total_characters: 0,
        translated_characters: 0
      }
    };

    // Update the game in the unifiedUserStore to keep the games list in sync
    const currentData = unifiedUserStore.getData();
    const updatedData = currentData.map(row => 
      row.game_id === gameId ? { ...row, games: updatedGame } : row
    );
    unifiedUserStore.setData(updatedData);

    return updatedGame;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update game';
    console.error("updateGameInfo - error:", err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update game name specifically
 */
export async function updateGameName(
  gameId: string,
  newName: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<Game> {
  return updateGameInfo(gameId, { name: newName }, toast);
}
